import { environment } from '@environments/environment';

export const getParameterIfFilterVisible = (
	filters: { [filterId: string]: { selectedPrimaryValues: string[]; selectedSecondaryValues: string[] } },
	visibleFilterIds: string[],
	filterId: string,
	ifEmpty: string = '',
	head: string = '@',
	separator: string = '@@',
	tail: string = '@',
): string => {
	let result = '';

	// if filter is active for the selected layer
	if (visibleFilterIds.includes(filterId)) {
		result = `FILTER_${filterId.toUpperCase()}:${hasValues(filterId, filters)}`;

		if (hasValues(filterId, filters) == '1') {
			// geoserver doesn't handle commas in params, has to be escaped as \,
			// oracle needs a '' to represent ' in values (es. ANZOLA DELL'EMILIA -> ANZOLA DELL''EMILIA)
			let values = getValues(filterId, filters, ifEmpty, head, separator, tail, true).replaceAll(',', '%5C,');
			result += ';' + `INPUT_${filterId.toUpperCase()}:'${values}'`;
		}
	}

	return result;
};

/**
 * Returns '0' or '1' based on the selected values for the filter.
 * @param key key of the fitlers dictionary
 * @returns '0' if no values where selected for the filter, '1' otherwise
 */
export const hasValues = (
	key: string,
	filters: { [filterId: string]: { selectedPrimaryValues: string[]; selectedSecondaryValues: string[] } },
): string => {
	if (!(key in filters)) {
		return '0';
	}

	let valuesCount = filters[key].selectedSecondaryValues.length;

	// if list is empty
	if (valuesCount == 0) {
		return '0';
	}

	// if list has one element but that element is '' (es. when 'tutti' is selected)
	if (valuesCount == 1 && filters[key].selectedSecondaryValues[0].trim() == '') {
		return '0';
	}

	return '1';
};

/**
 * Gets the value of a filter already formatted to feed the mapviewer
 * @param key key of the fitlers dictionary
 * @param ifEmpty what to return if the filter has no values
 * @param head string to place at the head of the string
 * @param separator string to use as a separator for multiple values
 * @param tail string to place at the head of the string
 * @returns the formatted string
 */
export const getValues = (
	key: string,
	filters: { [filterId: string]: { selectedPrimaryValues: string[]; selectedSecondaryValues: string[] } },
	ifEmpty: string = '',
	head: string = '@',
	separator: string = '@@',
	tail: string = '@',
	replaceApostrophe: boolean = false,
) => {
	if (!(key in filters)) {
		return ifEmpty;
	}

	let values = filters[key].selectedSecondaryValues.filter((v) => v != '');
	if (replaceApostrophe) values = values.map((value) => value.replaceAll("'", "''"));

	// return the empty value if the list is empty
	if (values.length == 0) {
		return ifEmpty;
	}

	return head + values.join(separator) + tail;
};

export const getFlussiPopUpContent = (info: any) => {
	let popupContent = '';
	popupContent =
		'<div  style="font-family: Titillium Web">' +
		`<b>Postazione ${info.app_id}</b><br/>` +
		`Strada: ${info.app_tratto} <br/>` +
		`Transiti totali: ${info.num_transiti.toLocaleString('de-DE')} <br/>`;

	if (info.tgm != undefined) {
		popupContent += `Transiti giornalieri medi: ${Math.round(info.tgm).toLocaleString('de-DE')} <br/>`;
	}

	/* if (info.vel_media != undefined) {
		popupContent += `Velocità media: ${info.vel_media} <br/>`;
	} */

	popupContent += '</div>';
	return popupContent;
};

/**
 * Get content to display in map popup
 */
export const getLimitiPopUpContent = (info: any, operation: number) => {
	let popupContent = '';
	if (operation === 1) {
		popupContent =
			'<div style="font-family: Titillium Web">' +
			'<b>[' +
			info.numero +
			'] ' +
			info.nome +
			'</b><br/>' +
			'Classificazione amministrativa: ' +
			info.cls_amm +
			'<br/>' +
			'Portata: ' +
			limiti_getPortata(info) +
			'<br/>' +
			'Sagoma: ' +
			limiti_getSagoma(info) +
			'<br/>' +
			'Tipologia: ' +
			limiti_getTipologia(info) +
			'<br/></div>';
	} else if (operation === 3) {
		popupContent =
			'<b>' +
			info.tipolimpunt_desc +
			'</b><br/>' +
			'Attraversamento: ' +
			info.limpunt_desc +
			'<br/>' +
			'Strada: [' +
			info.str_num +
			'] ' +
			info.str_des +
			'<br/>' +
			'Classificazione amministrativa: ' +
			info.cls_amm +
			'<br/>' +
			'Altezza: ' +
			info.limpunt_altezza_m +
			'<br/>' +
			'Larghezza: ' +
			info.limpunt_larghezza_m +
			'<br/>' +
			'Carico: ' +
			info.limpunt_carico_t +
			'<br/>';

		if (typeof info.foto_1 !== 'undefined') {
			popupContent +=
				`Foto 1: <a href="${environment.contextRoot}/api/limPuntualiFoto/` +
				info.foto_1 +
				'.jpg" target="_blank">Clicca per visualizzare</a><br/>';

			if (typeof info.foto_2 !== 'undefined') {
				popupContent +=
					`Foto 2: <a href="${environment.contextRoot}/api/limPuntualiFoto/` +
					info.foto_2 +
					'.jpg" target="_blank">Clicca per visualizzare</a><br/>';
			}
		} else {
			popupContent += 'Foto: Nessuna foto<br/>';
		}
	}

	return popupContent;
};

export const limiti_getPortata = (info: any) => {
	if (info.tperc_100t == 'IT' || info.tperc_100t == 'TC') {
		return '100 t';
	} else if (info.tperc_75t == 'IT' || info.tperc_75t == 'TC') {
		return '75 t';
	} else if (info.tperc_56t == 'IT' || info.tperc_56t == 'TC') {
		return '56 t';
	} else if (info.tperc_40t == 'IT' || info.tperc_40t == 'TC') {
		return '40 t';
	} else if (info.tperc_33t == 'IT' || info.tperc_33t == 'TC') {
		return '33 t';
	} else if (
		info.tperc_33t == '\\' ||
		info.tperc_40t == '\\' ||
		info.tperc_56t == '\\' ||
		info.tperc_75t == '\\' ||
		info.tperc_100t == '\\'
	) {
		return 'Non Specificato';
	} else return 'Non Transitabile';
};

export const limiti_getSagoma = (info: any) => {
	if (info.tperc_7m == 'IT' || info.tperc_7m == 'TC') {
		return '7 m';
	} else if (info.tperc_6m == 'IT' || info.tperc_6m == 'TC') {
		return '6 m';
	} else if (info.tperc_6m == '\\' || info.tperc_7m == '\\') {
		return 'Non Specificato';
	} else return 'Non Transitabile';
};

export const limiti_getTipologia = (info: any) => {
	let array = new Array();

	if (info.tperc_pali == 'IT' || info.tperc_pali == 'TC') {
		array.push('Pali');
	}
	if (info.tperc_carri_ferr == 'IT' || info.tperc_carri_ferr == 'TC') {
		array.push('Carri Ferroviari');
	}
	if (info.tperc_macchine_a == 'IT' || info.tperc_macchine_a == 'TC') {
		array.push('Macchine Agricole');
	}
	if (info.tperc_coils_lami == 'IT' || info.tperc_coils_lami == 'TC') {
		array.push('Coils');
	}
	if (info.tperc_macchine_op == 'IT' || info.tperc_macchine_op == 'TC') {
		array.push('Macchine Op. 72 T');
	}
	if (info.tperc_prefabbr_25108 == 'IT' || info.tperc_prefabbr_25108 == 'TC') {
		array.push('Prefabbricati 25 m x 108 T');
	}
	if (info.tperc_prefabbr_2575 == 'IT' || info.tperc_prefabbr_2575 == 'TC') {
		array.push('Prefabbricati 25 m x 75 T');
	}
	if (info.tperc_prefabbr_35108 == 'IT' || info.tperc_prefabbr_35108 == 'TC') {
		array.push('Prefabbricati 35 m x 108 T');
	}

	let text = '';

	if (array.length == 0) {
		if (
			info.tperc_prefabbr_35108 == '\\' ||
			info.tperc_pali == '\\' ||
			info.tperc_carri_ferr == '\\' ||
			info.tperc_macchine_a == '\\' ||
			info.tperc_coils_lami == '\\' ||
			info.tperc_macchine_op == '\\' ||
			info.tperc_prefabbr_25108 == '\\' ||
			info.tperc_prefabbr_2575 == '\\'
		) {
			text = 'Non Specificato';
		} else text = 'Nessun veicolo speciale ammesso';
	} else {
		text += array[0];
		for (var i = 1; i < array.length; i++) {
			text += ', ' + array[i];
		}
	}
	return text;
};
